import React, {useState, useMemo} from 'react'
import {FormInputComponent} from "../../../components/form/Form";
import {useSupplierFormItem} from "../providers/SupplierFormItemProvider";
import {Button, Divider, InputNumber, Tag, Select, Empty} from "antd";
import FlipMove from "react-flip-move";
import SearchBrandsInput from "../../../components/form/SearchBrandsInput";
import {SearchTagsExpertise} from "../../talent-application/components/KeyClientsForm";
import {PictureOutlined, PlusOutlined, DeleteOutlined} from "@ant-design/icons";
import InfoTooltip from "../../../components/InfoTooltip";
import {useAPI} from "../../../config/API";
import {useDebounce} from "react-use";
import LoadingSpinner from "../../../components/LoadingSpinner";

const MAX_TAGS_COUNT = 12;
const MAX_BRANDS_COUNT = 6;

const ServiceOfferingsForm = () => {

    const {
        profileItem,
        profileIndex,
        onModelChange,
        refresh,
        isEditing
    } = useSupplierFormItem()

    let {
        disciplines = [],
        skills = [],
        industries = [],
        brands = [],
        deliverables = [],
        faqs = [],
        ...props
    } = profileItem;

    const allTags = TB.concat(
        TB.map(disciplines || [], item => ({...item, type: 'disciplines', value: `discipline-${item.id}`})),
        TB.map(skills || [], item => ({...item, type: 'skills', value: `skill-${item.id}`})),
        TB.map(industries || [], item => ({...item, type: 'industries', value: `industry-${item.id}`}))
    );

    const addDeliverable = () => {
        const updatedDeliverables = [...(deliverables || [])];
        updatedDeliverables.push({ name: '', description: '' });
        onModelChange({
            ...profileItem,
            deliverables: updatedDeliverables
        });
    };

    const updateDeliverable = (deliverableIndex, field, value) => {
        const updatedDeliverables = [...(deliverables || [])];
        if (!updatedDeliverables[deliverableIndex]) return;

        updatedDeliverables[deliverableIndex] = {
            ...updatedDeliverables[deliverableIndex],
            [field]: value
        };
        onModelChange({
            ...profileItem,
            deliverables: updatedDeliverables
        });
    };

    const removeDeliverable = (deliverableIndex) => {
        const updatedDeliverables = [...(deliverables || [])];
        updatedDeliverables.splice(deliverableIndex, 1);
        onModelChange({
            ...profileItem,
            deliverables: updatedDeliverables
        });
    };

    const addFaq = () => {
        const updatedFaqs = [...(faqs || [])];
        updatedFaqs.push({ question: '', answer: '' });
        onModelChange({
            ...profileItem,
            faqs: updatedFaqs
        });
    };

    const updateFaq = (faqIndex, field, value) => {
        const updatedFaqs = [...(faqs || [])];
        if (!updatedFaqs[faqIndex]) return;

        updatedFaqs[faqIndex] = {
            ...updatedFaqs[faqIndex],
            [field]: value
        };
        onModelChange({
            ...profileItem,
            faqs: updatedFaqs
        });
    };

    const removeFaq = (faqIndex) => {
        const updatedFaqs = [...(faqs || [])];
        updatedFaqs.splice(faqIndex, 1);
        onModelChange({
            ...profileItem,
            faqs: updatedFaqs
        });
    };

    return (
        <div className="flex flex-col space-y-6">
            {/* Basic Info */}
            <div className="field-section flex-row">
                <div className="field px-3.5 basis-6/12">
                    <FormInputComponent
                        fieldName="name"
                        value={profileItem.name}
                        type="text"
                        label="Service Name"
                        placeholder="e.g., Digital Marketing Strategy"
                        maxLength={255}
                        required={true}
                    />
                </div>
                <div className="field px-3.5 basis-6/12">
                    <FormInputComponent
                        fieldName="tag_line"
                        value={profileItem.tag_line}
                        type="text"
                        label="Tag Line"
                        placeholder="Brief catchy description"
                        maxLength={1000}
                        minLength={30}
                        required={true}
                    />
                </div>
            </div>

            {/* Image */}
            <div className="field-section flex-row">
                <div className="field px-3.5 basis-8/12">
                    <FormInputComponent
                        fieldName="image_path"
                        value={profileItem.image_path}
                        label="Service Image"
                        type="pixie"
                        ratio="16:9"
                        circular={false}
                        width={400}
                        required={true}
                    />
                </div>
            </div>

            {/* Fee Information */}
            <div className="field-section flex-row items-center">
                <div className="field px-3.5 basis-6/12 [&>.form-group]:flex [&>.form-group]:flex-col">
                    <FormInputComponent
                        fieldName="fee_type"
                        value={profileItem.fee_type}
                        type="select"
                        label="Fee Type"
                        required={true}
                        options={{
                            hourly: 'Hourly',
                            fixed: 'Fixed Fee',
                            retainer: 'Retainer',
                            performance: 'Performance-based'
                        }}
                    />
                </div>
                <div className="field px-3.5 basis-6/12">
                    <FormInputComponent
                        fieldName="fee_amount"
                        value={profileItem.fee_amount}
                        label="Starting Price"
                        type='antd-input'
                        className="[&>.ant-input-number]:border-transparent [&>.ant-input-number]:rounded-none"
                        required={true}
                    >
                        <InputNumber
                            disabled={!isEditing}
                            min={0}
                            max={10000000}
                            formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                            placeholder="Enter amount"
                            style={{ width: '100%' }}
                        />
                    </FormInputComponent>
                </div>
            </div>

            {/* Summary */}
            <div className="field-section flex-row">
                <div className="field px-3.5 basis-12/12">
                    <FormInputComponent
                        fieldName="summary"
                        value={profileItem.summary}
                        type="editor"
                        label="Service Summary"
                        placeholder="Describe what this service offering includes..."
                        maxLength={10000}
                        required={false}
                    />
                </div>
            </div>

            {/* Process */}
            <div className="field-section flex-row">
                <div className="field px-3.5 basis-12/12">
                    <FormInputComponent
                        fieldName="process"
                        value={profileItem.process}
                        type="editor"
                        label="Process Description"
                        placeholder="Explain your process for delivering this service..."
                        maxLength={10000}
                        required={false}
                    />
                </div>
            </div>

            {/* Tags Section */}
            <Divider />
            <div className="space-y-4">
                <h4 className="text-lg font-semibold">Expertise & Tags</h4>

                {/* Display existing tags */}
                {allTags.length > 0 && (
                    <div className="field px-3.5">
                        <label className="col-form-label flex flex-row items-center gap-7">
                            <span>Selected Tags</span>
                            <span className={TB.mapClass("ml-auto text-xxs italic", {
                                "opacity-7": allTags.length < MAX_TAGS_COUNT,
                                "text-danger font-semibold opacity-1": allTags.length === MAX_TAGS_COUNT,
                            })}>
                                Max ({allTags.length} / {MAX_TAGS_COUNT})
                            </span>
                        </label>
                        <div className="discipline-skills-container">
                            <FlipMove typeName={null} maintainContainerHeight={true}>
                                {TB.map(allTags, (item, tagIndex) => {
                                    const colorMap = {
                                        industries: 'default',
                                        skills: 'geekblue',
                                        disciplines: 'purple'
                                    };

                                    return (
                                        <Tag
                                            key={item.value}
                                            color={colorMap[item.type]}
                                            closable={isEditing}
                                            onClose={() => {
                                                const currentTags = profileItem[item.type] || [];
                                                const filteredTags = TB.filter(currentTags, option => parseInt(option?.id) !== parseInt(item.id));
                                                onModelChange({
                                                    ...profileItem,
                                                    [item.type]: filteredTags
                                                });
                                            }}
                                            className="mr-3 my-1.5">
                                            {item.name}
                                        </Tag>
                                    );
                                })}
                            </FlipMove>
                        </div>
                    </div>
                )}

                {/* Tag search */}
                <div className="field px-3.5">
                    <SearchTagsExpertise
                        isDisabled={!isEditing}
                        isEditable={isEditing}
                        value={allTags}
                        maxCount={MAX_TAGS_COUNT}
                        onChange={params => {
                            onModelChange({
                                ...profileItem,
                                ...params
                            });
                        }}
                    />
                </div>

                {/* Hidden form fields for tags */}
                <FormInputComponent
                    type="hidden"
                    style={{display: 'none'}}
                    fieldName="industries"
                    value={profileItem.industries}
                />
                <FormInputComponent
                    type="hidden"
                    style={{display: 'none'}}
                    fieldName="skills"
                    value={profileItem.skills}
                />
                <FormInputComponent
                    type="hidden"
                    style={{display: 'none'}}
                    fieldName="disciplines"
                    value={profileItem.disciplines}
                />
                {/*<FormInputComponent*/}
                {/*    type="hidden"*/}
                {/*    style={{display: 'none'}}*/}
                {/*    fieldName="service_tags"*/}
                {/*    value={profileItem.service_tags}*/}
                {/*/>*/}
            </div>

            <Divider />

            {/* Services Section */}
            <div className="space-y-4">
                <h4 className="text-lg font-semibold">Services</h4>
                {/* Service Tags Display */}
                {profileItem.service_tags && profileItem.service_tags.length > 0 && (() => {
                    const groupedTags = {};
                    profileItem.service_tags.forEach(tag => {
                        if (!groupedTags[tag.category]) {
                            groupedTags[tag.category?.name || tag.category] = [];
                        }
                        groupedTags[tag.category?.name || tag.category].push(tag);
                    });

                    return (
                        <div className="field px-3.5">
                            <label className="col-form-label flex flex-row items-center gap-7">
                                <span>Selected Service Tags</span>
                                <span className={TB.mapClass("ml-auto text-xxs italic", {
                                    "opacity-7": profileItem.service_tags.length < MAX_TAGS_COUNT,
                                    "text-danger font-semibold opacity-1": profileItem.service_tags.length === MAX_TAGS_COUNT,
                                })}>
                                    Max ({profileItem.service_tags.length} / {MAX_TAGS_COUNT})
                                </span>
                            </label>
                            <div className="space-y-4">
                                {Object.keys(groupedTags).map(categoryName => (
                                    <div key={categoryName} className="category-group">
                                        <h6 className="text-sm font-medium text-gray-600 mb-2">{categoryName}</h6>
                                        <div className="discipline-skills-container">
                                            <FlipMove typeName={null} maintainContainerHeight={true}>
                                                {TB.map(groupedTags[categoryName], (tag, tagIndex) => (
                                                    <Tag
                                                        key={tag.id}
                                                        color="cyan"
                                                        closable={isEditing}
                                                        onClose={() => {
                                                            const updatedTags = profileItem.service_tags.filter(t => t.id !== tag.id);
                                                            onModelChange({
                                                                ...profileItem,
                                                                service_tags: updatedTags
                                                            });
                                                        }}
                                                        className="mr-3 my-1.5"
                                                    >
                                                        {tag.name}
                                                    </Tag>
                                                ))}
                                            </FlipMove>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })()}

                {/* Service Tags Search */}
                <div className="field px-3.5">
                    <SearchServiceTags
                        isDisabled={!isEditing}
                        isEditable={isEditing}
                        value={profileItem.service_tags || []}
                        maxCount={MAX_TAGS_COUNT}
                        onChange={(updatedTags) => {
                            console.log('cnakjs', updatedTags)
                            onModelChange({
                                ...profileItem,
                                service_tags: updatedTags
                            });
                        }}
                    />
                </div>
                <FormInputComponent
                    type="hidden"
                    style={{display: 'none'}}
                    fieldName="service_tags"
                    value={profileItem.service_tags}
                />
            </div>

                {/* Brands Section */}
            <Divider />
            <div className="space-y-4">
                <h4 className="text-lg font-semibold">Brands</h4>
                <div className="field px-3.5">
                    <label className="col-form-label flex flex-row items-center gap-7">
                        <span>Brands Worked With</span>
                        {brands.length > 0 && (
                            <span className={TB.mapClass("ml-auto text-xxs italic", {
                                "opacity-7": brands.length < MAX_BRANDS_COUNT,
                                "text-danger font-semibold opacity-1": brands.length === MAX_BRANDS_COUNT,
                            })}>
                                Max ({brands.length} / {MAX_BRANDS_COUNT})
                            </span>
                        )}
                    </label>

                    {brands.length > 0 && (
                        <div className="items-stretch flex flex-col mt-5">
                            <div className="discipline-skills-container">
                                <FlipMove typeName={null} maintainContainerHeight={true}>
                                    {brands.map((brand, brandIndex) => {
                                        const brandKey = TB.slugify(`brand-${profileIndex}-${(brand.id || brand.domain || brand.name)}`);
                                        return (
                                            <div key={brandKey} className="bg-gray-50 ml-8 mb-8 self-stretch flex flex-row items-center p-1">
                                                <div id={brandKey}>
                                                    <div className="w-24 self-center">
                                                        {(brand.logo || brand.icon) ?
                                                            <img className="w-full max-w-full" src={brand.logo || brand.icon} alt={brand.name}/> :
                                                            <PictureOutlined className="w-full max-w-full" style={{fontSize: 56}}/>
                                                        }
                                                    </div>
                                                </div>
                                                <InfoTooltip type="white" target={brandKey} placement="top">
                                                    <div className="flex flex-col gap-1">
                                                        <p className="p-0 text-left m-0 text-lg">{brand.name || brand.domain}</p>
                                                        {brand.name && brand.domain && (
                                                            <p className="p-0 text-left m-0 text-sm text-info whitespace-nowrap">
                                                                {brand.domain}
                                                            </p>
                                                        )}
                                                    </div>
                                                </InfoTooltip>
                                                {isEditing && (
                                                    <div className="self-start ml-2 flex flex-col">
                                                        <div onClick={() => {
                                                            const updatedBrands = [...brands];
                                                            updatedBrands.splice(brandIndex, 1);
                                                            onModelChange({
                                                                ...profileItem,
                                                                brands: updatedBrands
                                                            });
                                                        }} className="clickable fa fa-times-circle text-danger self-start"/>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </FlipMove>
                            </div>
                            <Divider className="mt-0 mb-4"/>
                        </div>
                    )}

                    {isEditing && (
                        <div className="mt-1">
                            <SearchBrandsInput
                                isDisabled={brands.length >= MAX_BRANDS_COUNT}
                                placeholder={brands.length < MAX_BRANDS_COUNT ? "Search for brands" : "Max brands limit reached"}
                                autoClearSearchValue={true}
                                shouldReset={true}
                                value={null}
                                disabledOptions={TB.map(brands, brand => brand.id || brand.brandId || brand.domain)}
                                onChange={(brand) => onModelChange({
                                    ...profileItem,
                                    brands: [...brands, brand]
                                })}
                            />
                        </div>
                    )}

                    {/* Hidden form field for brands */}
                    <FormInputComponent
                        type="hidden"
                        style={{display: 'none'}}
                        fieldName="brands"
                        value={profileItem.brands}
                    />
                </div>
            </div>

            {/* Deliverables Section */}
            <Divider />
            <div className="space-y-4">
                <h4 className="text-lg font-semibold">Deliverables</h4>
                {TB.map(deliverables || [], (deliverable, deliverableIndex) => (
                    <div key={deliverableIndex} className="border p-4 rounded bg-gray-50">
                        <div className="flex justify-between items-center mb-3">
                            <span className="font-medium">Deliverable {deliverableIndex + 1}</span>
                            {isEditing && (
                                <Button
                                    type="button"
                                    danger
                                    size="small"
                                    icon={<DeleteOutlined />}
                                    onClick={() => removeDeliverable(deliverableIndex)}
                                />
                            )}
                        </div>
                        <div className="field-section flex-row">
                            <div className="field px-3.5 basis-6/12">
                                <FormInputComponent
                                    fieldName={`deliverables[${deliverableIndex}].name`}
                                    value={deliverable.name}
                                    type="text"
                                    label="Name"
                                    placeholder="e.g., Marketing Strategy Document"
                                    maxLength={255}
                                    required={false}
                                    onChange={(value) => updateDeliverable(deliverableIndex, 'name', value)}
                                />
                            </div>
                            <div className="field px-3.5 basis-6/12">
                                <FormInputComponent
                                    fieldName={`deliverables[${deliverableIndex}].description`}
                                    value={deliverable.description}
                                    type="textarea"
                                    label="Description"
                                    placeholder="Describe what this deliverable includes..."
                                    maxLength={10000}
                                    required={false}
                                    onChange={(value) => updateDeliverable(deliverableIndex, 'description', value)}
                                />
                            </div>
                        </div>
                    </div>
                ))}
                {isEditing && (
                    <Button
                        type="button"
                        icon={<PlusOutlined />}
                        onClick={addDeliverable}
                    >
                        Add Deliverable
                    </Button>
                )}

                {/* Hidden form field for deliverables */}
                <FormInputComponent
                    type="hidden"
                    style={{display: 'none'}}
                    fieldName="deliverables"
                    value={profileItem.deliverables}
                />
            </div>

            {/* FAQs Section */}
            <Divider />
            <div className="space-y-4">
                <h4 className="text-lg font-semibold">Frequently Asked Questions</h4>
                {TB.map(faqs || [], (faq, faqIndex) => (
                    <div key={faqIndex} className="border p-4 rounded bg-gray-50">
                        <div className="flex justify-between items-center mb-3">
                            <span className="font-medium">FAQ {faqIndex + 1}</span>
                            {isEditing && (
                                <Button
                                    type="button"
                                    danger
                                    size="small"
                                    icon={<DeleteOutlined />}
                                    onClick={() => removeFaq(faqIndex)}
                                />
                            )}
                        </div>
                        <div className="field-section flex-col space-y-3">
                            <div className="field px-3.5">
                                <FormInputComponent
                                    fieldName={`faqs[${faqIndex}].question`}
                                    value={faq.question}
                                    type="text"
                                    label="Question"
                                    placeholder="What question do clients frequently ask?"
                                    maxLength={255}
                                    required={false}
                                    onChange={(value) => updateFaq(faqIndex, 'question', value)}
                                />
                            </div>
                            <div className="field px-3.5">
                                <FormInputComponent
                                    fieldName={`faqs[${faqIndex}].answer`}
                                    value={faq.answer}
                                    type="textarea"
                                    label="Answer"
                                    placeholder="Provide a helpful answer..."
                                    maxLength={10000}
                                    required={false}
                                    onChange={(value) => updateFaq(faqIndex, 'answer', value)}
                                />
                            </div>
                        </div>
                    </div>
                ))}
                {isEditing && (
                    <Button
                        type="button"
                        icon={<PlusOutlined />}
                        onClick={addFaq}
                    >
                        Add FAQ
                    </Button>
                )}

                {/* Hidden form field for faqs */}
                <FormInputComponent
                    type="hidden"
                    style={{display: 'none'}}
                    fieldName="faqs"
                    value={profileItem.faqs}
                />
            </div>
        </div>
    )
}

const SearchServiceTags = ({onChange, value, isDisabled, isEditable, maxCount = 12}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const selectedTagIds = TB.map(value || [], 'id');

    const [isSearchReady, cancel] = useDebounce(
        () => {
            setDebouncedSearch(search);
        },
        650, [search]
    );

    const {
        data: serviceTags,
        isLoading,
        isFetching,
        error
    } = useAPI(['fetchServiceOfferingTags', {search: debouncedSearch}], {
        // enabled: debouncedSearch?.length > 0,
        initialData: TB.orderBy(TB.values(TB.reduce(laravel.service_tag_categories, (result, value, key) => {
            if (!value.category) {
                return result
            }
            if (result[value.category?.id]) {
                result[value.category?.id].service_tags.push(TB.pick(value, ['id', 'name', 'description', 'display_index']));
                result[value.category?.id].service_tags = TB.orderBy(result[value.category?.id]?.service_tags, 'display_index', 'asc')
            }else {
                result[value.category?.id] = {
                    ...value.category,
                    service_tags: [
                        TB.pick(value, ['id', 'name', 'description', 'display_index'])
                    ]
                }
            }
            return result
        }, {})), 'display_index', 'asc'),
        keepPreviousData: true,
        onSuccess: (data) => {
            console.log('onSuccess', data);
        }
    });

    const isSearching = search?.length > 0 && (debouncedSearch !== search || isFetching);

    const searchOptions = useMemo(() => {
        if (isLoading || !serviceTags) {
            return [];
        }

        const options = [];
        serviceTags.forEach(category => {
            if (category.service_tags && category.service_tags.length > 0) {
                category.service_tags.forEach(tag => {
                    options.push({
                        value: tag.id,
                        label: tag.name,
                        name: tag.name,
                        id: tag.id,
                        category: category.name
                    });
                });
            }
        });

        return options;
    }, [serviceTags, isLoading, search, isFetching]);

    const groupedOptions = useMemo(() => {
        if (!searchOptions.length) return [];

        const grouped = {};
        searchOptions.forEach(option => {
            if (!grouped[option.category]) {
                grouped[option.category] = [];
            }
            grouped[option.category].push(option);
        });

        return Object.keys(grouped).map(categoryName => ({
            label: categoryName,
            options: grouped[categoryName]
        }));
    }, [searchOptions]);

    console.log('SearchServiceTags', {serviceTags, searchOptions});
    return (
        <div>
            <Select
                size="large"
                disabled={isDisabled || value?.length >= maxCount}
                placeholder={value?.length < maxCount ? "Search and select related services..." : "Max services limit reached"}
                // open={isOpen}
                showArrow={value?.length < maxCount}
                // onFocus={() => setIsOpen(true)}
                // onBlur={() => setIsOpen(false)}
                searchValue={search || ''}
                onSearch={setSearch}
                showSearch={true}
                filterOption={false}
                value={null}
                notFoundContent={
                    isSearching ? (
                        <div className="flex flex-col items-center justify-center my-8 h-20 gap-2">
                            <LoadingSpinner size={'lg'} />
                            <p className="text-sm opacity-6 text-center">Loading results...</p>
                        </div>
                    ) : (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={search?.length ? "No services found." : 'Start typing to search'}
                        />
                    )
                }
                loading={isSearching}
                autoClearSearchValue={true}
                onSelect={(selectValue) => {
                    let option = TB.find(searchOptions, ['id', parseInt(selectValue)]);
                    console.log('option', option, selectValue);
                    const updatedTags = [...(value || []), option];
                    onChange(updatedTags);
                    setSearch('');
                }}
                style={{width: '100%'}}
            >
                {groupedOptions.map(group => (
                    <Select.OptGroup key={group.label} label={group.label}>
                        {group.options.map(option => (
                            <Select.Option
                                key={option.id}
                                value={option.id}
                                disabled={selectedTagIds.includes(option.id)}
                            >
                                {option.name}
                            </Select.Option>
                        ))}
                    </Select.OptGroup>
                ))}
            </Select>
        </div>
    );
};

export default ServiceOfferingsForm;
export { SearchServiceTags };

const styles = {
    fieldSection: {
        flexDirection: 'row'
    },
    field: {
        padding: '0 15px',
    },
};